import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import UserService from '../../services/UserService';
import SuperAdminService from '../../services/SuperAdminService';
import { USER_ROLES } from '../../constants/main';
import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import OrganizationForm, { emptyForm, emptyFeatureFlags } from '../../components/OrganizationForm';
import { UiContext } from '../../context/UiContext';
import { useUiStore } from '../../store';
import DotsLoader from '../../components/DotsLoader';

import classes from './styles.module.scss';

export default function OrganizationNewPage() {
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const [formValues, setFormValues] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [featureFlags, setFeatureFlags] = useState(emptyFeatureFlags);
  const [storageQuota, setStorageQuota] = useState('');
  const [seatQuota, setSeatQuota] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');

  const { data: currentUser, isLoading: isLoadingUser } = useQuery(
    'me',
    UserService.getMe
  );

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  useEffect(() => {
    setBreadcrumbsOptions([
      { label: 'Super Admin', to: '/super-admin' },
      { label: 'New Organization', to: '#' },
    ]);
  }, [setBreadcrumbsOptions]);

  const validateForm = useCallback((values) => {
    const errors = {};
    if (!values.name.trim()) {
      errors.name = 'Name is required';
    } else if (values.name.trim().length > 50) {
      errors.name = 'Must be 50 characters or less';
    }

    if (!values.billingAddress.trim()) {
      errors.billingAddress = 'Billing address is required';
    } else if (values.billingAddress.trim().length > 500) {
      errors.billingAddress = 'Must be 500 characters or less';
    }

    if (!values.city.trim()) {
      errors.city = 'City is required';
    }

    if (!values.state.trim()) {
      errors.state = 'State is required';
    }

    const zipRegex = /^[0-9-]{5,10}$/;
    if (!values.zip.trim()) {
      errors.zip = 'ZIP code is required';
    } else if (!zipRegex.test(values.zip.trim())) {
      errors.zip = 'ZIP must be 5-10 digits';
    }
    return errors;
  }, []);

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleFeatureFlagChange = (flag, value) => {
    setFeatureFlags((prev) => ({ ...prev, [flag]: value }));
  };

  const { mutateAsync: createOrganization, isLoading: isCreating } =
    useMutation((payload) => SuperAdminService.createOrganization(payload));

  const handleSubmit = async () => {
    const errors = validateForm(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    try {
      const result = await createOrganization({
        ...formValues,
        feature_flags: featureFlags,
        storage_quota: storageQuota && storageQuota.trim() ? parseInt(storageQuota.trim(), 10) : null,
        seat_quota: seatQuota && seatQuota.trim() ? parseInt(seatQuota.trim(), 10) : null,
        trial_ends_at: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
      });
      notifySuccess('Organization created');
      navigate(`/super-admin/organizations/${result.data?.id || result.id}`);
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to create organization'
      );
    }
  };

  const handleCancel = () => {
    navigate('/super-admin');
  };

  if (currentUser?.role !== USER_ROLES.SUPER_ADMIN) {
    return null;
  }

  if (isLoadingUser) {
    return (
      <Layout hasUserBlock>
        <div className={classes.loaderContainer}>
          <DotsLoader />
        </div>
      </Layout>
    );
  }

  return (
    <Layout hasUserBlock>
      <div className={classes.OrganizationNewPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[
                { label: 'Super Admin', to: '/super-admin' },
                { label: 'New Organization', to: '#' },
              ]}
            />
          </div>

          <OrganizationForm
            formValues={formValues}
            formErrors={formErrors}
            featureFlags={featureFlags}
            storageQuota={storageQuota}
            seatQuota={seatQuota}
            trialEndsAt={trialEndsAt}
            onFormChange={handleFormChange}
            onFeatureFlagChange={handleFeatureFlagChange}
            onStorageQuotaChange={setStorageQuota}
            onSeatQuotaChange={setSeatQuota}
            onTrialEndsAtChange={setTrialEndsAt}
          />

          <div className={classes.actions}>
            <OutlinedButton 
              onClick={handleCancel} 
              disabled={isCreating}
              style={{ minWidth: '280px' }}
            >
              Cancel
            </OutlinedButton>
            <FilledButton
              type="button"
              onClick={handleSubmit}
              disabled={isCreating}
              style={{ minWidth: '280px' }}
            >
              {isCreating ? 'Saving…' : 'Create organization'}
            </FilledButton>
          </div>
        </div>
      </div>
    </Layout>
  );
}
